import { PDFDocument } from 'pdf-lib';
import { ref, shallowRef } from 'vue';

/**
 * Framework-agnostic PDF merge logic for the PdfMerger POC.
 *
 * `mergePdfs` is a pure async function (easy to unit test); `usePdfMergePreview`
 * wraps it with the debounce + last-write-wins guard the live preview needs so a
 * superseded (slower) merge run can never clobber a newer one.
 */

/** A file staged for merging. `id` is a stable key used by the list + dnd. */
export interface MergeInput {
  id: string;
  file: File;
}

let idCounter = 0;
export const makeStagedItemId = () => `pdf-${idCounter++}`;

export const isPdfFile = (file: File) =>
  file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

export const DEFAULT_MAX_FILES = 20;

/**
 * True only for a drag that's carrying files from outside the browser.
 * `dataTransfer.types` includes `'Files'` for a real OS file drag, but not
 * for an in-page native drag (e.g. the sortable list reordering its own
 * rows) — `.files` itself isn't populated until `drop`, so `.types` is the
 * only signal available during `dragover`/`dragenter`.
 */
export const isExternalFileDrag = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

export type PdfBatchRejectReason = 'notPdf' | 'tooManyFiles' | 'tooLarge';

export interface AppendPdfFilesResult {
  items: MergeInput[];
  addedCount: number;
  rejectionReasons: PdfBatchRejectReason[];
}

export interface AppendPdfFilesOptions {
  maxBytes: number;
  maxFiles?: number;
}

export function appendPdfFiles(
  existing: MergeInput[],
  incoming: FileList | File[],
  options: AppendPdfFilesOptions,
): AppendPdfFilesResult {
  const { maxBytes, maxFiles = DEFAULT_MAX_FILES } = options;
  const items = [...existing];
  const rejectionReasons: PdfBatchRejectReason[] = [];
  const addReason = (reason: PdfBatchRejectReason) => {
    if (!rejectionReasons.includes(reason)) rejectionReasons.push(reason);
  };

  const incomingFiles = Array.from(incoming);
  const pdfFiles = incomingFiles.filter(isPdfFile);
  if (pdfFiles.length < incomingFiles.length) addReason('notPdf');

  let runningBytes = items.reduce((sum, item) => sum + item.file.size, 0);
  let addedCount = 0;

  for (const file of pdfFiles) {
    if (items.length >= maxFiles) {
      addReason('tooManyFiles');
      break;
    }
    if (runningBytes + file.size > maxBytes) {
      addReason('tooLarge');
      break;
    }
    items.push({ id: makeStagedItemId(), file });
    runningBytes += file.size;
    addedCount++;
  }

  return { items, addedCount, rejectionReasons };
}

export type PdfMergeErrorReason = 'encrypted' | 'unreadable';

export interface MergedFileInfo {
  id: string;
  pageCount: number;
}

export interface PerFileError {
  id: string;
  reason: PdfMergeErrorReason;
}

export interface MergeResult {
  /** The merged bytes, or `null` when no file could be read. */
  blob: Blob | null;
  /** Total pages in the merged document (sum of the readable files' pages). */
  pageCount: number;
  /** Files that merged successfully, with their individual page counts. */
  ok: MergedFileInfo[];
  /** Files that were excluded, with a reason. */
  errored: PerFileError[];
}

/**
 * Read a file's bytes. Prefers the modern `Blob.arrayBuffer()` but falls back to
 * `FileReader` for environments that lack it (notably jsdom in the test runner).
 */
const readBytes = (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') return file.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

const classifyError = (err: unknown): PdfMergeErrorReason => {
  const message = err instanceof Error ? err.message : String(err);
  return /encrypt/i.test(message) ? 'encrypted' : 'unreadable';
};

/**
 * Merge the given PDFs into one, in order. A file that fails to load (corrupt,
 * encrypted, non-PDF) is excluded and reported in `errored`; the remaining good
 * files still combine. Encrypted PDFs are treated simply as unreadable.
 */
export const mergePdfs = async (inputs: MergeInput[]): Promise<MergeResult> => {
  const merged = await PDFDocument.create();
  const ok: MergedFileInfo[] = [];
  const errored: PerFileError[] = [];

  for (const input of inputs) {
    try {
      const bytes = await readBytes(input.file);
      // No `ignoreEncryption` — an encrypted PDF should throw and be excluded.
      const src = await PDFDocument.load(bytes);
      const pages = await merged.copyPages(src, src.getPageIndices());
      pages.forEach((page) => merged.addPage(page));
      ok.push({ id: input.id, pageCount: src.getPageCount() });
    } catch (err) {
      errored.push({ id: input.id, reason: classifyError(err) });
    }
  }

  const blob =
    ok.length > 0
      ? new Blob([await merged.save()], { type: 'application/pdf' })
      : null;

  return { blob, pageCount: merged.getPageCount(), ok, errored };
};

export interface UsePdfMergePreviewOptions {
  /** Debounce window before a committed change triggers a re-merge. */
  debounceMs?: number;
}

/**
 * Reactive wrapper around `mergePdfs` for the live preview. Call `schedule` on
 * every committed change (add / remove / completed drop). The spinner-friendly
 * `isMerging` flag covers both the debounce wait and the merge itself, and a
 * monotonic run id ensures a stale result is discarded if a newer run started.
 */
export const usePdfMergePreview = (options: UsePdfMergePreviewOptions = {}) => {
  const debounceMs = options.debounceMs ?? 200;

  const result = shallowRef<MergeResult | null>(null);
  const isMerging = ref(false);

  let runId = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const run = async (inputs: MergeInput[]) => {
    const myRun = ++runId;
    try {
      const res = await mergePdfs(inputs);
      if (myRun !== runId) return; // superseded by a newer run — discard.
      result.value = res;
    } catch {
      if (myRun !== runId) return;
      result.value = { blob: null, pageCount: 0, ok: [], errored: [] };
    } finally {
      if (myRun === runId) isMerging.value = false;
    }
  };

  const schedule = (inputs: MergeInput[]) => {
    if (timer) clearTimeout(timer);
    if (inputs.length === 0) {
      // Nothing to merge — short-circuit to an empty result, no spinner.
      runId++;
      result.value = { blob: null, pageCount: 0, ok: [], errored: [] };
      isMerging.value = false;
      return;
    }
    isMerging.value = true; // Spinner covers the debounce window too.
    timer = setTimeout(() => void run(inputs), debounceMs);
  };

  const reset = () => {
    if (timer) clearTimeout(timer);
    runId++; // Invalidate any in-flight run.
    result.value = null;
    isMerging.value = false;
  };

  return { result, isMerging, schedule, reset };
};
